import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import {
  borderRadius,
  colors,
  tailwindDimensions,
  zIndex,
} from '@infinex/ui-constants';

/**
 * Combines multiple class values into a single string of class names. Uses
 * "clsx" and "tailwind-merge" to merge class names and handle conflicts.
 *
 * @param inputs - The class values to combine.
 * @returns A string of class names.
 */
export function cn(...inputs: ClassValue[]) {
  return tailwindMerge(clsx(inputs));
}

/**
 * An implementation of the `twMerge` function that’s aware of the Taurus theme
 * and custom classes.
 *
 * @see https://github.com/dcastil/tailwind-merge/blob/v3.4.0/docs/api-reference.md#extendtailwindmerge
 */
const tailwindMerge = extendTailwindMerge({
  extend: {
    theme: {
      color: [...Object.keys(colors.static), 'inherit'],
      radius: [...Object.keys(borderRadius), 'inherit'],
      spacing: ['inherit'],
    },
    classGroups: {
      'bg-color': [{ bg: Object.keys(colors.background) }],
      'border-color': [{ border: Object.keys(colors.border) }],
      'divide-color': [{ divide: Object.keys(colors.divider) }],
      'outline-color': [{ outline: Object.keys(colors.outline) }],
      'ring-color': [{ ring: Object.keys(colors.ring) }],
      fill: [{ fill: Object.keys(colors.fill) }],
      stroke: [{ stroke: Object.keys(colors.stroke) }],
      'text-color': [{ text: Object.keys(colors.text) }],
      'text-decoration-color': [{ decoration: Object.keys(colors.text) }],
      'max-h': [{ 'max-h': Object.keys(tailwindDimensions) }],
      'max-w': [{ 'max-w': Object.keys(tailwindDimensions) }],
      'min-h': [{ 'min-h': Object.keys(tailwindDimensions) }],
      'min-w': [{ 'min-w': Object.keys(tailwindDimensions) }],
      h: [{ h: Object.keys(tailwindDimensions) }],
      size: [{ size: Object.keys(tailwindDimensions) }],
      w: [{ w: Object.keys(tailwindDimensions) }],
      z: [{ z: Object.keys(zIndex) }],
    },
  },
});
